import { effect, inject, Injectable } from '@angular/core'
import { Address, Carrier, Checkout, Currency, Order, PaymentItem } from 'depoto-core/src/entities'
import { FormControl, FormGroup } from '@angular/forms'
import { CoreService } from './core.service'
import { isForbiddenError, SchemaUtil } from '../utils'
import { BaseDetail } from '../pages/_base-detail/base-detail'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { TitleService } from './title.service'
import { CheckoutService } from './entity-services/checkout.service'
import { DepotService } from './entity-services/depot.service'
import { PaymentService } from './entity-services/payment.service'
import { TagService } from './entity-services/tag.service'
import { SupplierService } from './entity-services/supplier.services'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { VatService } from './entity-services/vat.services'
import { CompanyCarrierService } from './entity-services/companyCarrier.service'

type StorageKey = { type: string; key: string }
@Injectable({
  providedIn: 'root',
})
export class OrderDetailService extends BaseDetail {
  private depotService = inject(DepotService)
  private tagService = inject(TagService)
  private vatService = inject(VatService)
  private companyCarrierService = inject(CompanyCarrierService)
  private supplierService = inject(SupplierService)
  private checkoutService = inject(CheckoutService)
  private paymentService = inject(PaymentService)

  public order: Order
  newCurrency: Currency

  public isCreating: boolean
  public loading = true
  public submitted: boolean
  isShippingAddressSameAsInvoice = true

  canEditPrice = false
  canCreateOrEdit = false
  canCreateOrEditNotes = false

  isEditedFiles = false
  isEditedAddress = false
  isEditedTags = false

  storeInvoiceAddressOnCustomer = false
  storeShippingAddressOnCustomer = false

  updateProcessStatus: string

  activeLink = ''

  orderForm = new FormGroup({
    billNumber: new FormControl({ value: 0, disabled: false }, []),
    vs: new FormControl({ value: 0, disabled: false }, []),
    reservationNumber: new FormControl({ value: 0, disabled: false }, []),
    rounding: new FormControl({ value: 0, disabled: false }, []),
    externalId: new FormControl({ value: '', disabled: false }, []),
  })

  readonly queryCheckouts = injectQuery(() => ({
    queryKey: ['checkouts', 'options'],
    queryFn: async () => {
      return await this.checkoutService
        .getCheckouts(
          { page: 1, sort: 'name', direction: 'asc' },
          { id: null, name: null, enabled: null, depots: { id: null, name: null } }
        )
        .then(result => result.items)
    },
    select: data => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.checkouts?.length) {
        // TODO CORE USER
        return []
      } else if (
        !this.checkoutService.doesUserHaveAllCheckouts(data as any[]) &&
        this.core.services.user.user?.checkouts?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.checkouts.filter(ch => ch.enabled) // TODO CORE USER
      } else {
        return (data as any[]).filter(ch => ch.enabled)
      }
    },
  }))

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'order-detail-options'],
    queryFn: async () => {
      return await this.depotService
        .getDepots(
          {
            sort: 'name',
            direction: 'asc',
          },
          { id: null, name: null, enabled: null }
        )
        .then(result => result.items)
    },
    select: data => {
      return this.depotService.getUserScopedDepots(data as any[])
    },
  }))

  readonly queryVats = injectQuery(() => ({
    queryKey: ['vats', 'options'],
    queryFn: async () => {
      return await this.vatService.getVats({}, { id: null, name: null, percent: null, default: null }).then(result => {
        return result.items
      })
    },
  }))

  readonly queryPayments = injectQuery(() => ({
    queryKey: ['payments', 'options'],
    queryFn: async () => {
      return await this.paymentService.getPayments({}, { id: null, name: null, type: { id: null } }).then(result => {
        return result.items
      })
    },
  }))

  readonly queryTags = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    queryFn: async () => {
      return await this.tagService
        .getTags({ sort: 'name' }, { id: null, name: null, type: null })
        .then(result => result.items)
    },
    select: data => (data as any[]).filter(tag => tag.type === 'order'),
  }))

  readonly queryCompanyCarriers = injectQuery(() => ({
    queryKey: ['companyCarriers', 'options'],
    queryFn: async () => {
      return await this.companyCarrierService
        .getCompanyCarriers(
          {},
          {
            id: null,
            carrier: SchemaUtil.carrier,
          }
        )
        .then(result => {
          return [
            ...new Map(
              (result?.items ?? []).map(companyCarrier => companyCarrier.carrier).map(d => [d.id, d])
            ).values(),
          ]
        })
    },
    select: data => {
      return (data as any)?.sort((a, b) => {
        if (a.name < b.name) return -1
        if (a.name > b.name) return 1
        return 0
      })
    },
  }))

  readonly queryCurrencies = injectQuery(() => ({
    queryKey: ['currencies', 'options'],
    queryFn: async () => {
      return await this.paymentService.getCurrencies({}, { id: null, name: null, ratio: null }).then(result => {
        return result.items
      })
    },
  }))

  readonly querySuppliers = injectQuery(() => ({
    queryKey: ['suppliers', 'options'],
    queryFn: async () => {
      return await this.supplierService
        .getSuppliers(
          {
            sort: 'name',
            direction: 'asc',
          },
          { id: null, name: null }
        )
        .then(result => {
          return result.items
        })
    },
  }))

  constructor(
    public core: CoreService,
    public route: ActivatedRoute,
    public router: Router,
    public translateService: TranslateService,
    private titleService: TitleService
  ) {
    super(route, router, core, translateService)
    effect(() => {
      this.queryCurrencies.data()
      this.queryDepotsOptions.data()
      this.queryCompanyCarriers.data()
      this.queryPayments.data()
      this.queryCheckouts.data()
      this.querySuppliers.data()
      this.queryVats.data()
      this.queryTags.data()
    })
  }

  public resetAddressStoreFlags(): void {
    this.storeInvoiceAddressOnCustomer = false
    this.storeShippingAddressOnCustomer = false
  }

  public fillFormValue() {
    this.orderForm.setValue({
      reservationNumber: this.order.reservationNumber || 0,
      billNumber: this.order.billNumber || 0,
      vs: this.order.vs || 0,
      rounding: this.order.rounding || 0,
      externalId: this.order.externalId || '',
    })
  }

  public getFormValue() {
    this.order.reservationNumber = this.orderForm.value.reservationNumber
    this.order.billNumber = this.orderForm.value.billNumber
    this.order.vs = this.orderForm.value.vs
    this.order.rounding = this.orderForm.value.rounding
    this.order.externalId = this.orderForm.value.externalId
    this.order.currency = this.newCurrency ? this.newCurrency : this.order.currency // todo try comment
  }

  public async refreshData(id?: number) {
    this.loading = true
    if (this.isCreating) {
      this.order.checkout =
        this.queryCheckouts.data() && this.queryCheckouts.data().length === 1
          ? this.queryCheckouts.data()[0]
          : new Checkout()
      if (this.core.services.user.user?.checkouts?.length) {
        // TODO CORE USER
        this.order.checkout = new Checkout(this.core.services.user.user.checkouts[0]) // TODO CORE USER
      }
      this.order.carrier =
        this.queryCompanyCarriers.data()?.length === 1 ? this.queryCompanyCarriers.data()[0] : new Carrier()
      this.order.currency = new Currency(
        this.queryCurrencies.data().find(currency => currency.id.toUpperCase() === 'CZK')
      )
      this.order.paymentItems =
        this.queryPayments.data()?.length === 1
          ? [new PaymentItem({ payment: this.queryPayments.data()[0], amount: 0, isPaid: false })]
          : [new PaymentItem({ amount: 0, isPaid: false })]
      this.fillFormValue()
    } else {
      try {
        const order = await this.core.services.order.getByIdOrThrow(id, SchemaUtil.order)
        this.order = order ? new Order(order) : null
        // Update title with product name
        if (this.order) {
          this.titleService.updateTitleWithObject(this.order.id, String(this.order.reservationNumber), 'title.order-id')
        }
      } catch (e) {
        console.warn(e)
        if (isForbiddenError(e)) {
          return this.onLoadError(403, { forbiddenRedirectRouteTo: ['/orders'] })
        }
        return this.onLoadError(404)
      }
      if (!this.order?.id) {
        return this.onLoadError(404)
      }
      if (!this.order.invoiceAddress) {
        this.order.invoiceAddress = new Address()
      }
      if (!this.order.shippingAddress) {
        this.order.shippingAddress = new Address()
      }
      this.canCreateOrEdit =
        this.core.services.user.hasRole('ROLE_ORDER_UPDATE') || // TODO CORE USER
        this.core.services.user.hasRole('ROLE_SUPER_ADMIN') || // TODO CORE USER
        this.core.services.user.hasRole('ROLE_ADMIN') // TODO CORE USER

      this.canCreateOrEditNotes = this.canCreateOrEdit

      // canceled orders are read-only regardless of roles
      if (this.isCancelled) {
        this.canCreateOrEdit = false
        this.canCreateOrEditNotes = false
        this.canEditPrice = false
      }

      // console.log('isEditable=', this.order.isEditable, '; roles are Ok=', this.canCreateOrEdit)

      this.isShippingAddressSameAsInvoice = this.order?.invoiceAddress?.id === this.order?.shippingAddress.id
      this.sortOrderItems()
      this.fillFormValue()
    }
    this.loading = false
  }

  get isCancelled(): boolean {
    return this.order?.processStatus?.id === 'cancelled'
  }

  sortOrderItems() {
    this.order.items = [
      ...this.order.items.filter(item => item.type !== 'shipping' && item.type !== 'payment'),
      ...this.order.items.filter(item => item.type === 'payment'),
      ...this.order.items.filter(item => item.type === 'shipping'),
    ]
  }
}
